import React, { forwardRef } from 'react';
import { useRecoilState } from 'recoil';
import { useSharedGetReviewState } from '../hooks/actions/useReviewState';
import { HoneyloveReviewSet } from '../models/HoneyloveReview.model';
import { ViewProduct } from '../models/ViewProduct.model';
import { reviewPageFamily } from '../recoil/atoms';
import { CardBody } from './CardBody';
import { CardFooter } from './CardFooter';
import { Stars } from './Stars';
import { ImageLoader } from '../product_detail/components/ImageLoader';
import classNames from 'classnames';
import styles from './ProductReviewsMobile.module.scss';
import { Button } from '../root/components/buttons/Button';

interface Props {
  product: ViewProduct;
}

interface ReviewProps {
  name: string;
  isVerifiedBuyer: boolean;
  starRating: number;
  title: string;
  content: string;
  images: string[];
}

const pageCount = (reviewSet: HoneyloveReviewSet, itemsPerPage: number) => (reviewSet ? Math.ceil(reviewSet?.bottomline?.total_review / itemsPerPage) ?? 0 : 1);

const ProductReview: React.FC<ReviewProps> = ({ name, isVerifiedBuyer, starRating, title, content, images = [] }) => {
  return (
    <div className={styles.singleReviewContainer}>
      <div className={styles.singleReviewMetadataContainer}>
        <span className={styles.singleReviewName}>{name}</span>
        {isVerifiedBuyer && <span className={styles.singleReviewVerified}>Verified Buyer</span>}
      </div>
      <div className={styles.singleReviewStarContainer}>
        <Stars amount={starRating} />
      </div>
      <div className={styles.singleReviewContentContainer}>
        <span
          className={styles.singleReviewTitle}
          dangerouslySetInnerHTML={{
            __html: title,
          }}
        />
        <span
          className={styles.singleReviewContent}
          dangerouslySetInnerHTML={{
            __html: content,
          }}
        />
        <div className={styles.imageContainer}>
          {images.map((img, i) => (
            <ImageLoader key={i} alt={`Customer Review ${i}`} src={img} height={100} style={{ borderRadius: 5 }} allowClickToZoom={true} />
          ))}
        </div>
      </div>
    </div>
  );
};

const ITEMS_PER_PAGE = 3;

export const ProductReviewsMobile = forwardRef<HTMLDivElement, Props>(({ product }, ref) => {
  const [reviewPage, setReviewPage] = useRecoilState(reviewPageFamily(product.handle));
  const { reviewSet, allReviews } = useSharedGetReviewState({ productId: product.internalId, page: reviewPage, itemsPerPage: ITEMS_PER_PAGE });
  const totalPages = pageCount(reviewSet, ITEMS_PER_PAGE);

  const loading = !reviewSet?.bottomline;
  const showButton = loading || reviewPage < totalPages;

  return (
    <div ref={ref}>
      <CardBody>
        <div className={styles.reviewsContainer}>
          <span className={classNames(styles.title, styles.hr)}>WHAT CUSTOMERS SAY</span>
          {allReviews.map((r, i) => {
            return (
              <ProductReview
                key={i}
                title={r.title}
                name={r.user.display_name}
                content={r.content}
                isVerifiedBuyer={r.verified_buyer}
                starRating={r.score}
                images={r.images_data?.map((img) => img.original_url)}
              />
            );
          })}
          {showButton && <Button text="Show more" size={'small'} onClick={() => setReviewPage((prev) => prev + 1)} disabled={loading} />}
        </div>
      </CardBody>
      <CardFooter />
    </div>
  );
});
